import { createCanonicalDataset } from './schema.js';
import { attributeDefinitionsFromLegacy, extractIndexedAttributes, normalizeTimestamp, stableEventId } from './normalization.js';
import { buildCaseExtensions, indexSideRowsByTask } from './extensions.js';

function firstNonEmpty(...values) {
  return values.find(v => v !== undefined && v !== null && String(v) !== '') ?? '';
}

/**
 * Canonicalise the event-row format currently shared by Jira and ServiceNow.
 * This is deliberately a compatibility boundary: platform extraction can remain
 * unchanged while the downstream Process Advisor engine migrates to canonical data.
 */
export function legacyEventLogToCanonical({
  platform,
  eventLog = [],
  taskAttributes = [],
  eventAttributes = [],
  model = {},
  metadata = {},
  issueComments = [],
  issueWorklogs = [],
} = {}) {
  const caseMap = new Map();
  const events = [];
  const commentsByTask = indexSideRowsByTask(issueComments);
  const worklogsByTask = indexSideRowsByTask(issueWorklogs);
  const canonicalCaseAttributeDefinitions = attributeDefinitionsFromLegacy(taskAttributes, 'ta');

  (eventLog || []).forEach((row, index) => {
    const caseKey = String(firstNonEmpty(row.task, row.case_key, row.caseKey, row.number, row.task_id));
    if (!caseKey) return;
    const caseId = String(firstNonEmpty(row.task_id, row.case_id, row.caseId, row.sys_id, caseKey));
    const timestamp = normalizeTimestamp(firstNonEmpty(row.timestamp, row.sys_created_on, row.created));
    const caseAttrs = extractIndexedAttributes(row, 'ta');
    const eventAttrs = extractIndexedAttributes(row, 'ea');

    const existing = caseMap.get(caseId) || {
      id: caseId,
      key: caseKey,
      summary: firstNonEmpty(row.summary, row.short_description),
      createdAt: normalizeTimestamp(firstNonEmpty(row.create_time, row.created_at, row.opened_at)),
      closedAt: normalizeTimestamp(firstNonEmpty(row.close_time, row.closed_at, row.resolved_at)),
      attributes: {},
      metrics: {},
      source: {
        nativeId: caseId,
        nativeKey: caseKey,
      },
      extensions: {},
    };
    existing.attributes = { ...existing.attributes, ...caseAttrs };
    if (!existing.summary) existing.summary = firstNonEmpty(row.summary, row.short_description);
    if (!existing.createdAt) existing.createdAt = normalizeTimestamp(firstNonEmpty(row.create_time, row.created_at, row.opened_at));
    if (!existing.closedAt) existing.closedAt = normalizeTimestamp(firstNonEmpty(row.close_time, row.closed_at, row.resolved_at));
    existing.source = {
      ...(existing.source || {}),
      nativeId: caseId,
      nativeKey: caseKey,
    };
    existing.extensions = buildCaseExtensions({
      caseKey,
      caseAttributes: existing.attributes,
      caseAttributeDefinitions: canonicalCaseAttributeDefinitions,
      commentsByTask,
      worklogsByTask,
    });
    caseMap.set(caseId, existing);

    events.push({
      id: stableEventId(platform, caseId, index, row),
      caseId,
      caseKey,
      activity: String(firstNonEmpty(row.activity, row.activity_name, row.state)),
      activityType: String(firstNonEmpty(row.activity_type, row.activityType, row.field)),
      timestamp,
      resource: String(firstNonEmpty(row.resource, row.user, row.author, 'N/A')),
      attributes: eventAttrs,
      legacy: {
        commentsStartIndex: row.comments_si ?? null,
        commentsFinishIndex: row.comments_fi ?? null,
        worklogStartIndex: row.worklog_si ?? null,
        worklogFinishIndex: row.worklog_fi ?? null,
      },
      source: {
        eventType: firstNonEmpty(row.event_type, row.eventType),
        field: firstNonEmpty(row.activity_type, row.activityType, row.field),
        nativeRecordId: firstNonEmpty(row.event_id, row.eventId, row.sys_id, row.history_id),
      },
    });
  });

  const cases = Array.from(caseMap.values());
  const caseById = new Map(cases.map(c => [c.id, c]));
  events.sort((a, b) => a.caseKey.localeCompare(b.caseKey) || a.timestamp.localeCompare(b.timestamp) || a.id.localeCompare(b.id));

  // Fill case start/end from observed events when extraction rows do not carry them.
  for (const event of events) {
    const c = caseById.get(event.caseId);
    if (!c) continue;
    if (!c.createdAt || event.timestamp < c.createdAt) c.createdAt = event.timestamp;
    if (!c.closedAt || event.timestamp > c.closedAt) c.closedAt = event.timestamp;
  }

  return createCanonicalDataset({
    platform,
    model,
    cases,
    events,
    caseAttributes: canonicalCaseAttributeDefinitions,
    eventAttributes: attributeDefinitionsFromLegacy(eventAttributes, 'ea'),
    metadata: {
      ...metadata,
      legacyCompatibility: {
        rowFormat: 'process-advisor-event-log-v1',
        taskAttributePrefix: 'ta',
        eventAttributePrefix: 'ea',
      },
    },
  });
}

/**
 * Bridge canonical data back into the row contract consumed by the current miner.
 * This allows canonical adoption to happen incrementally without a big-bang rewrite.
 */
export function canonicalToLegacyEventLog(dataset) {
  const caseById = new Map((dataset?.cases || []).map(c => [String(c.id), c]));
  return (dataset?.events || []).map(event => {
    const c = caseById.get(String(event.caseId)) || {};
    return {
      task: event.caseKey || c.key || c.id || event.caseId,
      task_id: c.source?.nativeId || c.id || event.caseId,
      activity: event.activity,
      activity_type: event.activityType,
      timestamp: normalizeTimestamp(event.timestamp),
      resource: event.resource || 'N/A',
      summary: c.summary || 'null',
      comments_si: event.legacy?.commentsStartIndex ?? null,
      comments_fi: event.legacy?.commentsFinishIndex ?? null,
      worklog_si: event.legacy?.worklogStartIndex ?? null,
      worklog_fi: event.legacy?.worklogFinishIndex ?? null,
      create_time: c.createdAt || '',
      close_time: c.closedAt || '',
      ...(c.attributes || {}),
      ...(event.attributes || {}),
    };
  });
}
